import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CodexRail } from "@/components/site/CodexRail";
import { useEffect } from "react";
import { ArrowLeft, Share2, ExternalLink, Play } from "lucide-react";
import { STREAMING_LINKS, formatReleaseDate, type StreamingPlatform, type Release, type TrackListing, type ReleaseCredit } from "@/lib/content";
import { getReleaseBySlug, getReleases } from "@/lib/music.functions";
import { wisdomForRelease } from "@/lib/cross-links";
import { newsLinkingTo } from "@/lib/news";
import { ReleaseCover } from "@/components/site/ReleaseCover";
import { RelatedRail } from "@/components/site/RelatedRail";
import { TopicChips } from "@/components/site/TopicChips";
import { PlayButton } from "@/components/site/PlayButton";
import { Countdown } from "@/components/site/Countdown";
import { PreSaveCTA } from "@/components/site/PreSaveCTA";
import { FavoriteButton } from "@/components/site/FavoriteButton";
import { absoluteUrl, SITE_URL } from "@/lib/site";
import { track } from "@/lib/analytics";
import { breadcrumbs, ldScript, musicReleaseNode, personRef } from "@/lib/schema";

import type { PlayerTrack } from "@/lib/player/PlayerContext";
import { trackMarketingReleaseView, trackMarketingListenClick, trackMarketingPresaveClick, trackMarketingOwnClick } from "@/lib/marketing/analytics";


export const Route = createFileRoute("/music/$slug/")({
  loader: async ({ params }) => {
    const [release, catalog] = await Promise.all([
      getReleaseBySlug({ data: { slug: params.slug } }),
      getReleases().catch(() => [] as Release[]),
    ]);
    if (!release) throw notFound();
    // Catalog neighbours + first-party writings that reference this release.
    const index = catalog.findIndex((c) => c.slug === release.slug);
    const prev = index > 0 ? catalog[index - 1] : null;
    const next = index >= 0 && index < catalog.length - 1 ? catalog[index + 1] : null;
    const writings = wisdomForRelease(release.slug).map((w) => ({
      slug: w.slug,
      title: w.title,
      category: w.category,
    }));
    const news = newsLinkingTo(`/music/${release.slug}`).map((n) => ({
      slug: n.slug,
      title: n.title,
      dek: n.dek,
      publishedAt: n.publishedAt,
    }));
    return {
      release,
      writings,
      news,
      prev: prev ? { slug: prev.slug, title: prev.title } : null,
      next: next ? { slug: next.slug, title: next.title } : null,
    };
  },
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">This release is temporarily unavailable.</p>
      <Link to="/music" className="mt-4 inline-block text-gold-luminous">← Return to the archive</Link>
    </div>
  ),
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Release not found · Buddha KA" }, { name: "robots", content: "noindex" }] };
    const r = loaderData.release;
    const ogImage = r.coverUrl ? absoluteUrl(r.coverUrl) : null;
    const canonical = absoluteUrl(`/music/${r.slug}`);
    const title = `${r.title} · Buddha KA`;
    const meta = [
      { title },
      { name: "description", content: r.statement },
      { property: "og:title", content: title },
      { property: "og:description", content: r.statement },
      { property: "og:type", content: r.type === "Single" ? "music.song" : "music.album" },
      { property: "og:url", content: canonical },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: r.statement },
      ...(r.releaseDate ? [{ property: "music:release_date", content: r.releaseDate }] : []),
      ...(ogImage ? [
        { property: "og:image", content: ogImage },
        { property: "og:image:alt", content: `${r.title} cover art` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: ogImage },
      ] : []),
    ];

    // JSON-LD MusicAlbum (albums/EPs/mixtapes) or MusicRecording (singles).
    // Canonical release node from the shared entity graph (unified @ids,
    // artist / imprint / catalog references), plus this page's citations.
    const ld: Record<string, unknown> = {
      "@context": "https://schema.org",
      ...musicReleaseNode(r as never, { withTracks: true }),
      ...(loaderData.writings?.length || loaderData.news?.length
        ? {
            subjectOf: [
              ...(loaderData.writings ?? []).map((w) => ({
                "@type": "Article",
                name: w.title,
                url: absoluteUrl(`/wisdom/${w.slug}`),
                author: personRef(),
              })),
              ...(loaderData.news ?? []).map((n) => ({
                "@type": "NewsArticle",
                name: n.title,
                url: absoluteUrl(`/news/${n.slug}`),
                datePublished: n.publishedAt,
                author: personRef(),
              })),
            ],
          }
        : {}),
    };

    return {
      meta,
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        ldScript(ld),
        ldScript(breadcrumbs([["Music", "/music"], [r.title, `/music/${r.slug}`]])),
      ],
    };
  },


  component: ReleaseDetail,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">Release not found.</p>
      <Link to="/music" className="mt-4 inline-block text-gold-luminous">← Return to the archive</Link>
    </div>
  ),
});

const STATUS_LABEL = {
  released: "",
  upcoming: "Upcoming",
  "in-progress": "In the studio",
} as const;

function ReleaseDetail() {
  const { release: r, writings, news, prev, next } = Route.useLoaderData() as {
    release: Release;
    writings: Array<{ slug: string; title: string; category: string }>;
    news: Array<{ slug: string; title: string; dek: string; publishedAt: string }>;
    prev: { slug: string; title: string } | null;
    next: { slug: string; title: string } | null;
  };
  const dateLabel = formatReleaseDate(r);
  const metaParts: string[] = [r.type];
  if (dateLabel) metaParts.push(r.status === "upcoming" ? `Releasing ${dateLabel}` : dateLabel);
  else if (STATUS_LABEL[r.status]) metaParts.push(STATUS_LABEL[r.status]);
  if (r.runtime) metaParts.push(r.runtime);

  const isUpcoming = r.status === "upcoming";

  useEffect(() => {
    track("release_view", { slug: r.slug, status: r.status });
    trackMarketingReleaseView(r.slug, r.status);
  }, [r.slug, r.status]);

  // Only show streaming platforms with a real release-level URL. We never fall
  // back to artist-level profiles: a "Listen on Spotify" button that lands on
  // the artist page instead of this album is a broken promise — before or
  // after release day.
  const platforms: StreamingPlatform[] = ["Spotify", "Apple Music", "YouTube", "SoundCloud", "Bandcamp"];
  const availableLinks = platforms
    .map((p) => ({ platform: p, url: r.links?.[p] }))
    .filter((x): x is { platform: StreamingPlatform; url: string } => Boolean(x.url));


  // Build a play queue: album tracks with audioUrl first, else fall back to preview.
  const queue: PlayerTrack[] = [];
  if (r.tracks?.length) {
    for (const t of r.tracks) {
      if (t.audioUrl) {
        queue.push({
          id: `${r.slug}:${t.slug ?? t.title}`,
          title: t.title,
          subtitle: r.title,
          audioUrl: t.audioUrl,
          coverUrl: r.coverUrl,
          href: t.slug ? `/music/${r.slug}/song/${t.slug}` : `/music/${r.slug}`,
        });
      }
    }
  }
  if (!queue.length && r.previewUrl) {
    queue.push({
      id: r.slug,
      title: `${r.title} · Preview`,
      subtitle: "Buddha KA",
      audioUrl: r.previewUrl,
      coverUrl: r.coverUrl,
      href: `/music/${r.slug}`,
    });
  }
  const headTrack = queue[0] ?? null;

  const upcomingDate = r.status === "upcoming" && r.releaseDate ? r.releaseDate : null;

  return (
    <article>
      <section className="relative overflow-hidden border-b hairline">
        <div className="absolute inset-0 -z-10 halo animate-halo" aria-hidden />
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-20 lg:py-24">
          <Link to="/music" className="mb-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold sm:mb-8">
            <ArrowLeft size={14} /> Music Archive
          </Link>
          <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
            <ReleaseCover
              coverUrl={r.coverUrl}
              label={`${r.title} cover art`}
              className="mx-auto w-full max-w-xs shadow-gold sm:max-w-md"
              alt={`${r.title} cover art`}
              loading="eager"
            />
            <div className="flex flex-col justify-center">
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold sm:text-xs">{metaParts.join(" · ")}</p>
              <h1 className="mt-3 font-serif text-4xl leading-tight sm:mt-4 sm:text-5xl md:text-6xl lg:text-7xl">{r.title}</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Artist:{" "}
                <Link to="/artist/buddha-ka" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">
                  Buddha KA
                </Link>
              </p>
              <p className="mt-5 max-w-xl font-serif text-base italic text-ivory/90 sm:mt-6 sm:text-lg">&ldquo;{r.statement}&rdquo;</p>

              {availableLinks.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2 sm:mt-8" aria-label="Listen on">
                  {availableLinks.map(({ platform, url }) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Listen to ${r.title} on ${platform}`}
                      onClick={() => {
                        track("music_interaction", { slug: r.slug, platform, action: "listen" });
                        trackMarketingListenClick({ platform, slug: r.slug, surface: "release_hero" });
                      }}
                      className="inline-flex min-h-11 items-center gap-1 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold"
                    >
                      {platform} <ExternalLink size={10} aria-hidden />
                    </a>
                  ))}

                </div>
              )}

              {isUpcoming && r.preSaveUrl && (
                <a
                  href={r.preSaveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    track("release_presave_click", { slug: r.slug, platform: "hyperfollow", placement: "release_hero" });
                    trackMarketingPresaveClick({ slug: r.slug, platform: "hyperfollow", surface: "release_hero" });
                  }}
                  className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-base font-semibold text-obsidian shadow-gold sm:mt-8 sm:w-auto"
                >
                  Pre-save {r.title} <ExternalLink size={14} aria-hidden />
                </a>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-3 sm:mt-8">
                {headTrack && (
                  <PlayButton
                    track={headTrack}
                    queue={queue}
                    label={queue.length > 1 ? "Play album" : "Play preview"}
                  />
                )}
                {r.purchaseUrl && (
                  <a
                    href={r.purchaseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      track("release_purchase_click", { slug: r.slug });
                      trackMarketingOwnClick({ slug: r.slug, type: "release", surface: "release_hero" });
                    }}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border border-gold px-5 py-3 text-sm font-medium text-gold-luminous hover:bg-gold hover:text-obsidian"
                  >
                    <ExternalLink size={14} /> {r.purchaseLabel ?? "Buy now"}
                  </a>
                )}

                <button
                  type="button"
                  onClick={() => {
                    if (typeof navigator !== "undefined" && navigator.share) {
                      navigator.share({ title: `${r.title} · Buddha KA`, url: window.location.href }).catch(() => {});
                    } else if (typeof navigator !== "undefined" && navigator.clipboard) {
                      navigator.clipboard.writeText(window.location.href).catch(() => {});
                    }
                  }}
                  className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-3 text-sm hover:border-gold hover:text-gold"
                >
                  <Share2 size={14} /> Share
                </button>
                <FavoriteButton kind="music" slug={r.slug} />
              </div>

              {upcomingDate && (
                <div className="mt-8">
                  <Countdown target={upcomingDate} label={`${r.title} releases in`} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {isUpcoming && (
        <div className="mx-auto max-w-3xl px-4 pt-10 sm:px-6 sm:pt-14">
          <PreSaveCTA slug={r.slug} title={r.title} releaseDate={r.releaseDate} preSaveUrl={r.preSaveUrl} preSaveLinks={r.links} />
        </div>
      )}



      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-6 sm:py-20 lg:grid-cols-[2fr_1fr] lg:gap-16">
        <div className="space-y-12">
          {r.tracks && r.tracks.length > 0 && (
            <section>
              <h2 className="font-serif text-3xl">Tracklist</h2>
              <ol className="mt-6 divide-y hairline border-y hairline">
                {r.tracks.map((t: TrackListing, i: number) => {
                  const trackForPlayer: PlayerTrack | null = t.audioUrl
                    ? {
                        id: `${r.slug}:${t.slug ?? t.title}`,
                        title: t.title,
                        subtitle: r.title,
                        audioUrl: t.audioUrl,
                        coverUrl: r.coverUrl,
                        href: t.slug ? `/music/${r.slug}/song/${t.slug}` : `/music/${r.slug}`,
                      }
                    : null;
                  return (
                    <li key={i} className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 py-4 sm:gap-4">
                      <span className="w-6 text-right text-gold tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                      <div className="min-w-0">
                        {t.slug ? (
                          <Link
                            to="/music/$slug/song/$song"
                            params={{ slug: r.slug, song: t.slug }}
                            className="block truncate font-serif text-base hover:text-gold-luminous sm:text-lg"
                          >
                            {t.title}
                          </Link>
                        ) : (
                          <span className="block truncate font-serif text-base sm:text-lg">{t.title}</span>
                        )}
                      </div>
                      {t.duration && (
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{t.duration}</span>
                      )}
                      {trackForPlayer ? (
                        <PlayButton track={trackForPlayer} queue={queue} variant="icon" />
                      ) : (
                        <span className="w-10" aria-hidden />
                      )}
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {r.credits && r.credits.length > 0 && (
            <section>
              <h2 className="font-serif text-3xl">Credits</h2>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {r.credits.map((c: ReleaseCredit, i: number) => (
                  <li key={i}>{c.role} · {c.name}</li>
                ))}
              </ul>
            </section>
          )}

          {r.lyricsExcerpts && r.lyricsExcerpts.length > 0 && (
            <section>
              <h2 className="font-serif text-3xl">Lyrics</h2>
              <div className="mt-4 space-y-4 text-sm text-muted-foreground">
                {r.lyricsExcerpts.map((line: string, i: number) => (
                  <p key={i} className="font-serif text-base italic text-ivory/80">{line}</p>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="space-y-6">
          <div className="rounded-xl border hairline bg-charcoal p-6">
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Mood</p>
            <p className="mt-3 font-serif text-lg">{r.mood.join(" · ")}</p>
            <p className="mt-4 text-xs uppercase tracking-[0.3em] text-gold">Frequency</p>
            <p className="mt-2 font-serif text-lg">{r.frequency}</p>
          </div>
          <nav className="rounded-xl border hairline bg-charcoal p-6" aria-label="Related pages">
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Explore</p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/artist/buddha-ka" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Buddha KA — official artist page</Link>
              </li>
              <li>
                <Link to="/music" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Full Buddha KA music catalog</Link>
              </li>
              <li>
                <Link to="/news" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Official release news</Link>
              </li>
              <li>
                <Link to="/about" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">About Buddha KA and KA Flow</Link>
              </li>
              <li>
                <Link to="/press" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Press kit and official bios</Link>
              </li>
              <li>
                <Link to="/activations" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Daily activations</Link>
              </li>
              <li>
                <Link to="/contact" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Booking and licensing contact</Link>
              </li>
            </ul>
          </nav>

          {news.length > 0 && (
            <nav className="rounded-xl border hairline bg-charcoal p-6" aria-label="Official news about this release">
              <p className="text-xs uppercase tracking-[0.3em] text-gold">Official newsroom</p>
              <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                {news.map((n) => (
                  <li key={n.slug}>
                    <Link to="/news/$slug" params={{ slug: n.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
                      {n.title}
                    </Link>
                    <span> — {n.dek}</span>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {writings.length > 0 && (
            <nav className="rounded-xl border hairline bg-charcoal p-6" aria-label="Writings about this release">
              <p className="text-xs uppercase tracking-[0.3em] text-gold">Written about in Wisdom</p>
              <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                {writings.map((w) => (
                  <li key={w.slug}>
                    <Link to="/wisdom/$slug" params={{ slug: w.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
                      {w.title}
                    </Link>
                    <span> — {w.category}</span>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </aside>

      </div>

      {(prev || next) && (
        <nav aria-label="Catalog navigation" className="mx-auto flex max-w-7xl flex-wrap justify-between gap-4 px-4 pb-8 text-sm sm:px-6">
          {prev ? (
            <Link to="/music/$slug" params={{ slug: prev.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
              ← Previous release: {prev.title}
            </Link>
          ) : <span />}
          {next && (
            <Link to="/music/$slug" params={{ slug: next.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
              Next release: {next.title} →
            </Link>
          )}
        </nav>
      )}

      <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <TopicChips tags={[...(r.mood ?? []), r.type]} text={[r.title, r.statement].filter(Boolean).join(" ")} className="mb-10" />
        </div>
        <CodexRail entityKind="release" entityRef={r.slug} heading="Concepts this release carries" />
        <RelatedRail kind="music" slug={r.slug} heading="Keep going" />
      </div>
    </article>
  );
}
